'use client';

import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { Badge, DrawerCollapsible, MetaDefault } from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  componentExpectsBehavioralVerification,
  readAiComponentVerificationEvidence,
} from '../utils/aiComponentVerificationEvidence';

type AiComponentVerificationEvidenceProps = {
  component: AiComponentDto;
};

export function AiComponentVerificationEvidence({
  component,
}: AiComponentVerificationEvidenceProps) {
  const t = useTranslations('aiApplications.componentDrawer.verification');
  const evidence = readAiComponentVerificationEvidence({
    metadata: component.metadata,
  });

  if (!evidence) {
    if (
      !componentExpectsBehavioralVerification(component.type) ||
      component.verificationStatus === 'SUPPRESSED'
    ) {
      return null;
    }

    return (
      <DrawerCollapsible title={t('title')} defaultOpen>
        <MetaDefault color="fg.muted">{t('notRecorded')}</MetaDefault>
      </DrawerCollapsible>
    );
  }

  return (
    <DrawerCollapsible title={t('title')} defaultOpen>
      <VStack gap={3} alignItems="stretch">
        <Badge variant="outline">
          {evidence.outcome === 'CONFIRMED'
            ? t('confirmed')
            : t('contradicted')}
        </Badge>
        <EvidenceField label={t('claim')} value={evidence.claim} />
        <EvidenceField label={t('stimulus')} value={evidence.stimulus} />
        <EvidenceField
          label={t('predictedSignal')}
          value={evidence.predictedSignal}
        />
        <EvidenceField
          label={t('observedResponse')}
          value={evidence.observedResponse || t('emptyObserved')}
        />
        {evidence.invocationVisibility ? (
          <EvidenceField
            label={t('invocation')}
            value={t(
              evidence.invocationVisibility === 'hidden'
                ? 'invocationHidden'
                : evidence.invocationVisibility === 'user_visible'
                  ? 'invocationUserVisible'
                  : 'invocationUnknown',
            )}
          />
        ) : null}
        {evidence.exchangeToolCallId ? (
          <EvidenceField
            label={t('exchange')}
            value={evidence.exchangeToolCallId}
          />
        ) : null}
      </VStack>
    </DrawerCollapsible>
  );
}

function EvidenceField({ label, value }: { label: string; value: string }) {
  return (
    <VStack gap={1} alignItems="stretch">
      <MetaDefault color="fg.muted">{label}</MetaDefault>
      <MetaDefault whiteSpace="pre-wrap">{value}</MetaDefault>
    </VStack>
  );
}
